import React from 'react'
import { FeedItem, actOnFeedItem, markSeen, togglePin, ignoreSender } from '../lib/api'
import OverflowMenu from './OverflowMenu'

function timeAgo(dateStr: string): string {
  const now = new Date()
  const date = new Date(dateStr)
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  if (seconds < 60) return 'now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d`
}

function typeLabel(type: string): string {
  const labels: Record<string, string> = {
    email_intelligence: 'Email',
    action_item: 'Action',
    stale_alert: 'Stale',
    meeting_summary: 'Meeting',
    contact_alert: 'Contact',
    financial_alert: 'Financial',
    status_change: 'Status',
    agent_insight: 'Insight',
  }
  return labels[type] || type
}

interface Props {
  item: FeedItem
  onAction: () => void
  onDismiss?: (id: number) => void
}

export default function FeedCard({ item, onAction, onDismiss }: Props) {
  const urgencyClass = item.urgency === 'critical' || item.urgency === 'high'
    ? `urgency-${item.urgency}` : ''
  const unseenClass = !item.seen ? 'unseen' : ''

  const handleSeen = async () => {
    if (!item.seen) await markSeen(item.id)
  }

  const act = async (action: string) => {
    await actOnFeedItem(item.id, action)
    if (action === 'dismissed' && onDismiss) onDismiss(item.id)
    else onAction()
  }

  const menuActions = [
    { label: item.acted_on ? '✓ Got it (done)' : '✓ Got it', onClick: () => act('acknowledged'), disabled: !!item.acted_on },
    { label: '↩ Respond', onClick: () => act('responded'), disabled: !!item.acted_on },
    { label: item.pinned ? '⚑ Unflag' : '⚑ Flag', onClick: () => togglePin(item.id).then(() => onAction()) },
    { label: '✕ Dismiss', onClick: () => act('dismissed'), disabled: !!item.acted_on, danger: true },
    ...(item.item_type === 'email_intelligence' && item.context?.from_email
      ? [{ label: '🚫 Ignore sender', onClick: () => ignoreSender(item.id).then(() => onAction()), danger: true }]
      : []),
  ]

  return (
    <div
      className={`feed-item ${urgencyClass} ${unseenClass}`}
      onClick={handleSeen}
    >
      <div className="feed-meta">
        <span className={`feed-type ${item.item_type}`}>
          {typeLabel(item.item_type)}
        </span>
        {item.client_name && (
          <span className="feed-client">{item.client_name}</span>
        )}
        <span className="feed-time">{timeAgo(item.event_at)}</span>
        <OverflowMenu actions={menuActions} />
      </div>

      <div className="headline">{item.headline}</div>

      {item.body && <div className="body">{item.body}</div>}

      {item.context?.waiting_on && (
        <div className="body" style={{ marginTop: 4 }}>
          Waiting on: {item.context.waiting_on}
          {item.context.due_date && ` · Due: ${item.context.due_date}`}
        </div>
      )}
    </div>
  )
}
